import React, { useState, useEffect } from "react";
import {
  Search,
  Loader2,
  FileText,
  Link as LinkIcon,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import websiteContentApi from "../services/websiteContentApi";

const WebsiteContentSearch = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [toast, setToast] = useState(null);
  const [limit, setLimit] = useState(10);
  const [minSimilarity, setMinSimilarity] = useState(0.3);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [lastQuery, setLastQuery] = useState("");
  const [expandedChunks, setExpandedChunks] = useState(new Set());

  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };

  // Auto-hide toast after 3 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setToast(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const handleSearch = async (e) => {
    if (e) {
      e.preventDefault();
    }

    if (!searchQuery.trim()) {
      showToast("Please enter a search query", "error");
      return;
    }

    try {
      setIsSearching(true);
      setSearchPerformed(true);
      setLastQuery(searchQuery);

      const response = await websiteContentApi.search(searchQuery, limit, minSimilarity);
      const payload = response?.data?.data || response?.data;
      const searchResults = payload?.results || [];

      setResults(searchResults);

      if (searchResults.length === 0) {
        showToast(
          "No results found. Try adjusting your query or lowering the similarity threshold.",
          "info",
        );
      } else {
        showToast(`Found ${searchResults.length} result(s)`, "success");
      }
    } catch (error) {
      console.error("Error searching website content:", error);
      showToast("Failed to search website content", "error");
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const getSimilarityColor = (similarity) => {
    if (similarity >= 0.8) return "text-green-700 bg-green-100";
    if (similarity >= 0.6) return "text-blue-700 bg-blue-100";
    if (similarity >= 0.5) return "text-yellow-700 bg-yellow-100";
    return "text-gray-700 bg-gray-100";
  };

  const toggleChunkExpansion = (chunkId) => {
    setExpandedChunks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(chunkId)) {
        newSet.delete(chunkId);
      } else {
        newSet.add(chunkId);
      }
      return newSet;
    });
  };

  return (
    <div className="space-y-2">
      {/* Compact Search Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
        <form onSubmit={handleSearch}>
          <div className="flex gap-2 items-start">
            {/* Search Input */}
            <div className="flex-1">
              <div className="relative">
                <Search
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
                  size={16}
                />
                <input
                  type="text"
                  placeholder="Search website content (e.g., 'matcha', 'coffee', 'store hours')"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isSearching}
                />
              </div>
            </div>

            {/* Compact Controls */}
            <div className="flex gap-2 items-center">
              <input
                type="number"
                min="1"
                max="50"
                value={limit}
                onChange={(e) => setLimit(parseInt(e.target.value) || 10)}
                className="w-16 px-2 py-2 border border-gray-300 rounded text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isSearching}
                title="Max results"
              />
              <input
                type="number"
                min="0"
                max="1"
                step="0.05"
                value={minSimilarity}
                onChange={(e) => setMinSimilarity(parseFloat(e.target.value) || 0.3)}
                className="w-16 px-2 py-2 border border-gray-300 rounded text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isSearching}
                title="Min similarity (0.0-1.0)"
              />
              <button
                type="submit"
                disabled={isSearching || !searchQuery.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors inline-flex items-center justify-center gap-1.5 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSearching ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>Searching</span>
                  </>
                ) : (
                  <>
                    <Search size={16} />
                    <span>Search</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Compact Results Section */}
      {searchPerformed && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Compact Results Header */}
          <div className="bg-gray-50 px-3 py-2 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-700">Query:</span>
              <span className="text-xs text-gray-900 font-semibold">"{lastQuery}"</span>
            </div>
            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold">
              {results.length} result{results.length !== 1 ? "s" : ""}
            </span>
          </div>

          {/* Compact Results List */}
          <div className="divide-y divide-gray-100">
            {isSearching ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={32} className="animate-spin text-blue-600" />
              </div>
            ) : results.length === 0 ? (
              <div className="text-center py-8">
                <FileText size={40} className="mx-auto text-gray-400 mb-2" />
                <h3 className="text-sm font-semibold text-gray-900 mb-1">No results found</h3>
                <p className="text-xs text-gray-600 max-w-md mx-auto">
                  Try lowering min similarity (currently {minSimilarity}) or check if content is
                  crawled.
                </p>
              </div>
            ) : (
              results.map((result, index) => (
                <div key={result.chunkId} className="p-3 hover:bg-gray-50 transition-colors">
                  {/* Compact Result Header */}
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-start gap-2 flex-1 min-w-0">
                      <span className="w-5 h-5 bg-blue-100 rounded flex items-center justify-center flex-shrink-0 text-xs font-bold text-blue-600">
                        {index + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <LinkIcon size={12} className="text-gray-400 flex-shrink-0" />
                          <a
                            href={result.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 hover:underline text-xs font-medium truncate"
                            title={result.url}
                          >
                            {result.url}
                          </a>
                        </div>
                        {result.title && (
                          <p className="text-xs text-gray-600 truncate mt-0.5">{result.title}</p>
                        )}
                      </div>
                    </div>

                    {/* Compact Similarity Score */}
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-bold flex-shrink-0 ${getSimilarityColor(result.similarity)}`}
                    >
                      {(result.similarity * 100).toFixed(0)}%
                    </span>
                  </div>

                  {/* Compact Chunk Text */}
                  <div className="ml-7">
                    <div
                      className="bg-gray-50 rounded p-2 border border-gray-200 cursor-pointer hover:border-gray-300 transition-colors"
                      onClick={() => toggleChunkExpansion(result.chunkId)}
                      title="Click to expand/collapse"
                    >
                      <p
                        className={`text-xs text-gray-700 leading-relaxed ${expandedChunks.has(result.chunkId) ? "" : "line-clamp-4"}`}
                      >
                        {result.chunkText}
                      </p>
                      {result.chunkText.length > 200 && (
                        <div className="text-xs text-blue-600 mt-1 font-medium">
                          {expandedChunks.has(result.chunkId)
                            ? "Click to collapse"
                            : "Click to expand"}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className="fixed bottom-4 right-4 z-50 animate-slide-up">
          <div
            className={`px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 ${
              toast.type === "success"
                ? "bg-green-500 text-white"
                : toast.type === "error"
                  ? "bg-red-500 text-white"
                  : "bg-blue-500 text-white"
            }`}
          >
            {toast.type === "success" ? (
              <CheckCircle size={20} />
            ) : toast.type === "error" ? (
              <AlertCircle size={20} />
            ) : (
              <AlertCircle size={20} />
            )}
            <span className="font-medium">{toast.message}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebsiteContentSearch;
